/**
 * The order ticket — which account, which stock, buy / sell / manage, nine order types, four ways to
 * size it, how long it lasts, its targets and stops, its rules, notes — then the card the system draws
 * for it: the exact orders, the risk, the checks. The ticket only drafts; nothing is sent until that
 * card is approved.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowLeft, ChartLine, Check, ChevronDown, ChevronRight, Plus, Search, X } from 'lucide-react'
import { create } from 'zustand'
import type { PfAccount, PortfolioLive } from '@shared/portfolio'
import type { SearchResult } from '@shared/types'
import { DEFAULT_INSURANCE } from '@shared/worker'
import { useExtendedQuotes, useLiveQuotes, useMarketSession } from '../hooks/useMarketData'
import { useOrder } from '../hooks/useOrder'
import { useWorker } from '../hooks/useWorker'
import { displaySymbol, normalizeSymbol } from '../lib/symbol'
import { api, ordersApi, workersApi } from './api'
import { PlanCard, TicketCard } from './Cards'
import { Btn, Card, Menu, cx, usd, type MenuItem } from './kit'
import { Choose, qtyText } from './Page'
import { useDemo } from './store'

/* ── what the ticket and the page share ──────────────────────────────── */

export type Side = 'buy' | 'sell' | 'manage'
/** Writing down a trade made somewhere else: no order is sent. */
const RECORD = 'record'
/** The card the system drew for what was filled in, and what it was drawn for. */
interface Drafted {
  kind: 'order' | 'worker'
  id: string
  title: string
  symbol: string
  side: Side
  account: string
}
/**
 * Where the ticket points, set from outside too (a holding pressed, "Record a holding"). `symbol` is the
 * app-wide code ("GOOGL.US"); `account` is an account's id or 'record' ('' until the accounts were seen).
 */
export const useTicket = create<{ symbol: string; side: Side; account: string; sheet: boolean; card: Drafted | null; seen: string | null | undefined; load: (symbol: string, side?: Side) => void }>((set) => ({
  symbol: '',
  side: 'buy',
  account: '',
  sheet: false,
  card: null,
  seen: undefined,
  load: (symbol, side) => set((s) => ({ symbol: normalizeSymbol(symbol), side: side ?? s.side, sheet: true })),
}))

const TYPES = [
  ['MKT', 'Market', 'Now, at the best price'],
  ['LMT', 'Limit', 'At your price or better'],
  ['STP', 'Stop', 'A market order once the price gets there'],
  ['STP LMT', 'Stop limit', 'A limit order once the price gets there'],
  ['TRAIL', 'Trailing stop', 'A stop that follows the price'],
  ['TRAIL LIMIT', 'Trailing stop limit', 'A trailing stop that sends a limit'],
  ['MIT', 'Market if touched', 'A market order once the price touches a level'],
  ['LIT', 'Limit if touched', 'A limit order once the price touches a level'],
  ['MOC', 'Market on close', 'At the closing price'],
] as const
type Kind = (typeof TYPES)[number][0]
/** The entries a worker can open with. */
const WORKER_ENTRY = new Set<Kind>(['MKT', 'LMT', 'STP', 'STP LMT'])
type Size = 'shares' | 'usd' | 'pct' | 'risk'
type Tif = 'DAY' | 'GTC' | 'GTD'
const TIFS: [Tif, string][] = [
  ['DAY', 'Day'],
  ['GTC', 'Until cancelled'],
  ['GTD', 'Until a date'],
]
type Unit = 'usd' | 'pct'
const UNITS = [
  ['pct', '%'],
  ['usd', '$'],
] as const
interface Target {
  price: string
  pct: string
}
interface Stop {
  price: string
  kind: 'stop' | 'limit' | 'trail'
  /** limit: the worst price it may sell at · trail: how far it follows. */
  extra: string
  unit: Unit
}
const STOPS: [Stop['kind'], string][] = [
  ['stop', 'Stop'],
  ['limit', 'Stop limit'],
  ['trail', 'Trailing'],
]
const WHEN = [
  ['tp', 'a target fills', 'target #'],
  ['above', 'the price rises to', 'price'],
  ['below', 'the price falls to', 'price'],
  ['sessions', 'sessions after the buy', 'sessions'],
  ['minutes', 'minutes after the buy', 'minutes'],
  ['earnings', 'before the next earnings', ''],
  ['time', 'a date and time (New York)', 'YYYY-MM-DD HH:MM'],
  ['daily', 'every day at (New York)', 'HH:MM'],
] as const
const THEN = [
  ['breakeven', 'move the stops to break-even', ''],
  ['stopTo', 'move the stops to', 'price'],
  ['trail', 'trail the stops by', 'distance'],
  ['close', 'close the position', ''],
  ['reduce', 'sell part of it', '% of the shares'],
  ['cancelEntry', 'cancel what has not been bought', ''],
  ['notify', 'tell me', 'message'],
] as const
interface Rule {
  when: (typeof WHEN)[number][0]
  whenVal: string
  then: (typeof THEN)[number][0]
  thenVal: string
}
const SESSION = { pre: 'Pre-market', regular: 'Market open', after: 'After hours', closed: 'Market closed' }
/** The most rows a plan takes. */
const MOST = { exits: 6, rules: 12 }

const num = (s: string) => {
  const n = Number(String(s).replace(',', '.').trim())
  return Number.isFinite(n) && n > 0 ? n : null
}
/** A price typed as a number, or relative to the entry: "+4%", "-2.5%". */
const priceOf = (s: string, ref: number) => {
  const t = s.replace(',', '.').trim()
  const m = t.match(/^([+-]?\d+(?:\.\d+)?)\s*%$/)
  if (m) return ref > 0 ? Math.round(ref * (1 + Number(m[1]) / 100) * 100) / 100 : null
  return num(t)
}
/** A distance as it is typed: "2%" of the price, "1.50" in dollars, "2 atr" in average daily ranges. */
const distanceOf = (s: string): { usd: number } | { pct: number } | { atr: number } | null => {
  const m = s.replace(',', '.').trim().match(/^\$?\s*(\d+(?:\.\d+)?)\s*(%|\$|atr)?$/i)
  const v = m ? Number(m[1]) : 0
  if (!m || !(v > 0)) return null
  return m[2] === '%' ? { pct: v } : m[2]?.toLowerCase() === 'atr' ? { atr: v } : { usd: v }
}
/** A share of the position, as a worker's ladder takes it. */
const portion = (pct: number) => (Number.isInteger(pct) ? { kind: 'portion' as const, num: pct, den: 100 } : { kind: 'portion' as const, num: Math.round(pct * 10), den: 1000 })
/** Now in New York, as a "good until" is written. */
const etNow = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/New_York' }).slice(0, 16)
const tail = (a: PfAccount) => (a.mask ? `··${a.mask.slice(-4)}` : '')
const text = (e: unknown, or: string) => (e instanceof Error ? e.message : or)
const say = (m: string) => useDemo.getState().say(m)

/* ── the ticket's own small parts ────────────────────────────────────── */

/** A small field inside the ticket. */
const field = 'h-7 min-w-0 rounded-[6px] border border-ws-line bg-ws-field px-2 text-[13px] tracking-normal tabular-nums transition-colors duration-150 focus:border-ws-focus'
/** One row of the ticket: what it is on the left, its value or its control on the right. */
const Line = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-h-9 items-center justify-between gap-3 text-[13px] tracking-normal">
    <span className="shrink-0 text-ws-fg-3">{label}</span>
    <span className="flex min-w-0 items-center gap-2">{children}</span>
  </div>
)
/** A yes or no, ticked. */
const Tick = ({ on, onChange, children, title }: { on: boolean; onChange: (v: boolean) => void; children: ReactNode; title?: string }) => (
  <button type="button" role="checkbox" aria-checked={on} title={title} onClick={() => onChange(!on)} className="flex h-6 items-center gap-2 text-left text-[12px] tracking-normal text-ws-fg-2 transition-colors hover:text-ws-fg">
    <span className={cx('flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[4px] border transition-colors duration-150', on ? 'border-white bg-white text-black' : 'border-ws-rule')}>{on && <Check size={10} strokeWidth={3} />}</span>
    {children}
  </button>
)
/** A part of the ticket that stays folded until it is wanted: its name, and how much is in it. */
function Fold({ title, hint, count, open, onToggle, children }: { title: string; hint: string; count?: number; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <div className="border-t border-ws-edge">
      <button type="button" aria-expanded={open} onClick={onToggle} className="flex h-10 w-full items-center gap-2 px-3 text-left text-[13px] tracking-normal text-ws-fg-2 transition-colors duration-150 hover:text-ws-fg">
        <ChevronRight size={13} strokeWidth={2} className={cx('shrink-0 text-ws-fg-4 transition-transform duration-150', open && 'rotate-90')} />
        {title}
        <span className="ml-auto text-[12px] text-ws-fg-4">{count ? count : hint}</span>
      </button>
      {open && <div className="space-y-2 px-3 pb-3">{children}</div>}
    </div>
  )
}
/** Take a row away. */
const Drop = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick} className="flex h-7 w-6 shrink-0 items-center justify-center rounded-[6px] text-ws-fg-4 transition-colors hover:bg-ws-raise hover:text-ws-fg">
    <X size={13} strokeWidth={2} />
  </button>
)
/** Add a row. */
const Add = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <button type="button" onClick={onClick} className="flex h-7 items-center gap-[6px] rounded-[6px] pr-2 text-[12px] tracking-normal text-ws-fg-3 transition-colors hover:text-ws-fg">
    <Plus size={13} strokeWidth={2} />
    {children}
  </button>
)

/** Find a stock by its symbol or its name: what you hold while nothing is typed, every exchange once it is. */
function SymbolSearch({ value, held, onSelect }: { value: string; held: { code: string; symbol: string; last: number | null }[]; onSelect: (code: string) => void }) {
  const shown = displaySymbol(value)
  const [q, setQ] = useState(shown)
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState(0)
  const [found, setFound] = useState<SearchResult[] | null>(null)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => setQ(shown), [shown])
  const term = q.trim()
  const typed = !!term && q !== shown
  // what is typed is looked up a moment after the last key
  useEffect(() => {
    if (!open || !typed) return setFound(null)
    let on = true
    const wait = window.setTimeout(() => void api.search(term).then((r) => on && (setFound(r.slice(0, 7)), setAt(0)), () => on && setFound([])), 220)
    return () => {
      on = false
      window.clearTimeout(wait)
    }
  }, [term, typed, open])
  const price = (code: string) => held.find((h) => h.code === code)?.last?.toFixed(2)
  const list = typed ? (found ?? []).map((r) => ({ code: r.symbol, symbol: r.code, name: r.name, right: price(r.symbol) ?? r.exchange })) : held.slice(0, 7).map((h) => ({ code: h.code, symbol: h.symbol, name: '', right: h.last?.toFixed(2) ?? '' }))
  useEffect(() => {
    if (!open) return
    const down = (e: PointerEvent) => !box.current?.contains(e.target as Node) && (setOpen(false), setQ(shown))
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [open, shown])
  const pick = (code: string) => (onSelect(code), setQ(displaySymbol(code)), setOpen(false))
  return (
    <div ref={box} className="relative">
      <label className="flex h-10 items-center gap-2 rounded-[8px] border border-ws-line bg-ws-field pl-[10px] pr-2 transition-colors duration-150 focus-within:border-ws-focus">
        <Search size={15} strokeWidth={2} className="shrink-0 text-ws-icon" />
        <input
          value={q}
          onFocus={(e) => (e.currentTarget.select(), setOpen(true), setAt(0))}
          onChange={(e) => (setQ(e.target.value.toUpperCase()), setOpen(true), setAt(0))}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') (e.preventDefault(), setAt((i) => Math.min(list.length - 1, i + 1)))
            else if (e.key === 'ArrowUp') (e.preventDefault(), setAt((i) => Math.max(0, i - 1)))
            else if (e.key === 'Enter' && list[at]) (pick(list[at].code), e.currentTarget.blur())
            // a symbol the search does not know can still be typed whole
            else if (e.key === 'Enter' && typed) (pick(normalizeSymbol(term)), e.currentTarget.blur())
            else if (e.key === 'Escape') (setOpen(false), setQ(shown), e.currentTarget.blur())
          }}
          placeholder="Search a stock — symbol or name"
          aria-label="Stock"
          className="h-full min-w-0 flex-1 text-[15px] font-medium tracking-[-0.01em] placeholder:font-normal placeholder:tracking-normal"
        />
      </label>
      {open && list.length > 0 && (
        <div className="absolute inset-x-0 top-[calc(100%+6px)] z-30 animate-ws-pop rounded-[10px] border border-[#2a2a2a] bg-[#141414] p-1 shadow-[0_12px_32px_rgba(0,0,0,0.6)]">
          {list.map((x, i) => (
            <button key={`${x.code}:${i}`} type="button" onPointerEnter={() => setAt(i)} onClick={() => pick(x.code)} className={cx('flex h-9 w-full items-center gap-3 rounded-[6px] px-2 text-left tracking-normal', i === at ? 'bg-ws-raise text-ws-fg' : 'text-ws-fg-2')}>
              <span className="min-w-[52px] shrink-0 font-medium tracking-[-0.01em] text-ws-fg">{x.symbol}</span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-ws-fg-3">{x.name}</span>
              <span className="shrink-0 text-[12px] tabular-nums text-ws-fg-2">{x.right}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── the ticket ──────────────────────────────────────────────────────── */

/**
 * The ticket itself, as one card. To an account that can be traded it drafts whatever was filled in —
 * one order, or a worker when there is a ladder, a rule or shares to manage — and shows the system's
 * card for it; Approve is on that card. For "Record only" it writes a trade down and sends nothing.
 */
export function Ticket({ all, scope, onChanged }: { all: PortfolioLive; scope: string | null; onChanged: () => void }) {
  const t = useTicket()
  const settings = useDemo((s) => s.settings)
  const page = useDemo((s) => s.symbol)
  const [kind, setKind] = useState<Kind>('MKT')
  const [sizeMode, setSizeMode] = useState<Size>('shares')
  const [size, setSize] = useState('')
  const [limit, setLimit] = useState('')
  const [stop, setStop] = useState('')
  const [trail, setTrail] = useState('')
  const [trailUnit, setTrailUnit] = useState<Unit>('pct')
  const [offset, setOffset] = useState('')
  const [tif, setTif] = useState<Tif>('DAY')
  const [till, setTill] = useState('')
  const [ext, setExt] = useState(false)
  const [targets, setTargets] = useState<Target[]>([])
  const [stops, setStops] = useState<Stop[]>([])
  /** The break-even step: null = as Settings has it, until it is ticked either way here. */
  const [even, setEven] = useState<boolean | null>(null)
  const [exitsExt, setExitsExt] = useState(false)
  const [rules, setRules] = useState<Rule[]>([])
  const [sl, setSl] = useState('')
  const [tp, setTp] = useState('')
  const [fee, setFee] = useState('')
  const [note, setNote] = useState('')
  const [open, setOpen] = useState({ exits: false, rules: false, notes: false })
  const [quote, setQuote] = useState<{ code: string; price: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // where the order goes: the account the page is narrowed to when it can trade, else the first that can
  const tradable = useMemo(() => all.accounts.filter((a) => a.tradable), [all.accounts])
  const can = (id: string) => tradable.some((a) => a.id === id)
  const want = scope && can(scope) ? scope : scope === 'manual' ? RECORD : (tradable[0]?.id ?? RECORD)
  const tradableKey = tradable.map((a) => a.id).join(',')
  useEffect(() => {
    const s = useTicket.getState()
    const valid = s.account === RECORD || can(s.account)
    // what was chosen is kept while the page stays where it was; narrowing the page to another account moves the ticket with it
    const keep = s.seen === undefined || s.seen === scope ? valid : valid && s.account !== RECORD && !scope
    useTicket.setState({ seen: scope, account: keep ? s.account : want })
  }, [tradableKey, scope]) // eslint-disable-line react-hooks/exhaustive-deps
  const dest = t.account || want
  const account: PfAccount | null = tradable.find((a) => a.id === dest) ?? null
  const recording = !account
  const selling = t.side === 'sell'
  const buy = !selling
  const manage = t.side === 'manage' && !recording

  const code = t.symbol.trim() ? normalizeSymbol(t.symbol) : ''
  const sym = displaySymbol(code)
  const name = sym || '—'
  const session = useMarketSession()
  const extMap = useExtendedQuotes(code ? [code] : [], session)
  const liveMap = useLiveQuotes(code ? [code] : [])
  const holding = all.holdings.find((h) => h.code === code) ?? null
  const part = holding?.parts.find((p) => p.account === (account ? account.id : 'manual')) ?? null
  const held = part?.quantity ?? 0
  const minded = account ? (part?.workers.reduce((a, w) => a + w.open, 0) ?? 0) : 0
  // what an order may sell: not the shares a worker minds, not the shares that belong to a copy
  const sellable = Math.max(0, held - minded - (part?.copyQty ?? 0))
  // Robinhood holds fractions of a share and a worker can manage them; IBKR takes whole shares only
  const fractions = account?.broker === 'robinhood'
  const manageable = !account ? 0 : fractions ? Math.round(sellable * 1e6) / 1e6 : Math.floor(sellable + 1e-9)

  // the card on show, and how it stands: a draft still waits, anything else was decided on the card
  const card = t.card
  const { ticket } = useOrder(card?.kind === 'order' ? card.id : null)
  const { worker } = useWorker(card?.kind === 'worker' ? card.id : null)
  const status = !card ? null : card.kind === 'order' ? (ticket?.id === card.id ? ticket.status : null) : worker?.id === card.id ? worker.status : null
  const decided = status != null && status !== 'draft'
  const approved = decided && status !== 'rejected' && status !== 'expired'

  useEffect(() => {
    if (!code) return setQuote(null)
    let on = true
    api.quote(code).then((q) => on && setQuote({ code, price: q.price }), () => on && setQuote(null))
    return () => {
      on = false
    }
  }, [code])
  // the notes kept on the stock
  useEffect(() => {
    setSl(holding?.stopLoss != null ? String(holding.stopLoss) : '')
    setTp(holding?.takeProfit != null ? String(holding.takeProfit) : '')
  }, [code, holding?.stopLoss, holding?.takeProfit])
  // a new stock, a new side, another account: the amount starts again
  useEffect(() => setSize(''), [code, t.side, dest])
  // recording knows two kinds of trade; "manage" is only for shares that are there, and starts with all of them
  useEffect(() => {
    if (recording && kind !== 'MKT' && kind !== 'LMT') setKind('MKT')
    if (card || t.side !== 'manage') return
    if (recording || manageable <= 0) return useTicket.setState({ side: 'buy' })
    setSizeMode('shares')
    setKind('MKT')
    setSize(String(manageable))
    setOpen((o) => ({ ...o, exits: true }))
  }, [recording, t.side, code, dest, manageable, !card]) // eslint-disable-line react-hooks/exhaustive-deps
  const modes: [Size, string][] = manage ? [['shares', 'Shares to manage']] : recording ? [['shares', 'Shares'], ['usd', 'USD']] : buy ? [['shares', 'Shares'], ['usd', 'USD'], ['pct', '% cash'], ['risk', 'Risk $']] : [['shares', 'Shares'], ['usd', 'USD'], ['pct', '% held']]
  useEffect(() => {
    if (!modes.some((m) => m[0] === sizeMode)) setSizeMode('shares')
  }) // eslint-disable-line react-hooks/exhaustive-deps
  // what the server said about the last try no longer holds once the ticket is changed
  useEffect(() => setError(null), [code, t.side, dest, kind, sizeMode, size, limit, stop, trail, trailUnit, offset, tif, till, ext, targets, stops, rules, even, exitsExt])

  // a held stock's price comes with the portfolio itself (the broker's feed); otherwise the market data feed
  const last = holding?.last ?? liveMap[code]?.price ?? extMap[code]?.price ?? (quote?.code === code ? quote.price : 0)
  const withLimit = kind === 'LMT' || kind === 'STP LMT' || kind === 'LIT'
  const withStop = kind === 'STP' || kind === 'STP LMT'
  const touch = kind === 'MIT' || kind === 'LIT'
  const trailing = kind === 'TRAIL' || kind === 'TRAIL LIMIT'
  // the price the order is sized and its exits measured against
  const entry = (withLimit && kind !== 'LIT' ? num(limit) : null) ?? (withStop || touch ? num(stop) : null) ?? (last > 0 ? last : 0)

  // a sale has no exits of its own
  const exitsOn = !recording && buy && targets.length + stops.length > 0
  const insured = even ?? settings?.insurance?.on ?? DEFAULT_INSURANCE.on
  // a plan with a ladder, a special stop, rules, risk sizing, the break-even step or exits in extended hours runs as a worker
  const asWorker = !recording && buy && (manage || targets.length > 1 || stops.length > 1 || stops.some((s) => s.kind !== 'stop') || rules.length > 0 || sizeMode === 'risk' || (insured && targets.length > 0 && stops.length > 0) || (exitsExt && exitsOn))

  const stopPrices = stops.map((s) => priceOf(s.price, entry)).filter((x): x is number => x != null)
  const nearest = stopPrices.length ? Math.max(...stopPrices.filter((p) => p < entry), 0) || null : null
  const n = num(size) ?? 0
  // a fraction of a share goes only where the broker takes one: a plain market order in regular hours
  const plain = fractions && !asWorker && !exitsOn && kind === 'MKT' && !ext
  const cut = (x: number) => (recording || plain ? Math.floor(x * 1e6 + 1e-6) / 1e6 : Math.floor(x + 1e-9))
  const shares = !n ? 0 : sizeMode === 'shares' ? n : !(entry > 0) ? 0 : sizeMode === 'usd' ? cut(n / entry) : sizeMode === 'pct' ? cut(buy ? ((account?.buyingPower ?? 0) * n) / 100 / entry : (sellable * Math.min(n, 100)) / 100) : nearest ? Math.floor(n / (entry - nearest)) : 0
  const total = shares * entry
  const fraction = shares > 0 && Math.abs(shares - Math.round(shares)) > 1e-9

  const problems: string[] = []
  if (account && code && shares > 0) {
    if (asWorker && !manage && !WORKER_ENTRY.has(kind)) problems.push('A ladder or rules need a market, limit, stop or stop-limit entry.')
    if (ext && !manage && kind !== 'LMT' && kind !== (fractions ? 'LIT' : 'STP LMT')) problems.push('Extended hours: limit orders only.')
    if (!manage && tif === 'GTD' && !/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(till)) problems.push('Until a date needs a date and a time.')
    if (fraction && (manage ? !fractions : !plain || tif !== 'DAY')) problems.push(fractions ? 'A fraction of a share goes only as a day market order.' : 'This account takes whole shares only.')
    if (selling && shares > sellable + 1e-9) problems.push(`More than the ${qtyText(sellable)} you can sell here.`)
    if (manage && shares > manageable + 1e-9) problems.push(`More than the ${qtyText(manageable)} you can hand over here.`)
    if (!manage && withLimit && !num(limit)) problems.push('A limit order needs its price.')
    if (!manage && (withStop || touch) && !num(stop)) problems.push(touch ? 'It needs the price to touch.' : 'A stop order needs its price.')
    if (!manage && trailing && !num(trail)) problems.push('A trailing stop needs its distance.')
  }
  if (!recording && sizeMode === 'risk' && n > 0 && nearest == null) problems.push('Sizing by risk needs a stop below.')
  if (manage && shares > 0 && !targets.length && !stops.length && !rules.length) problems.push('Add a target, a stop or a rule for these shares.')
  if (account?.blocked) problems.push(account.blocked)
  // said, but left to the card to judge: the broker knows what the account can carry
  const over = account && !account.blocked && buy && !manage && code && shares > 0 && account.buyingPower != null && total > account.buyingPower + 0.005 ? `More than this account’s buying power (${usd(account.buyingPower)}).` : null
  const ready = !!code && shares > 0 && (kind === 'MKT' || kind === 'MOC' || entry > 0) && !problems.length

  /** The order as the order desk takes it: one order, with one stop and one target at most. */
  const orderSpec = (): { spec: unknown } | { error: string } => {
    const quantity = sizeMode === 'usd' ? (buy ? { kind: 'notional', usd: n } : { kind: 'shares', shares }) : sizeMode === 'pct' ? (buy ? { kind: 'notional', usd: ((account!.buyingPower ?? 0) * n) / 100 } : n >= 100 ? { kind: 'all' } : { kind: 'percent', pct: n }) : { kind: 'shares', shares: n }
    const t0 = exitsOn && targets[0] ? priceOf(targets[0].price, entry) : null
    const s0 = exitsOn && stops[0] ? priceOf(stops[0].price, entry) : null
    if (exitsOn && targets[0] && t0 == null) return { error: 'Target 1 needs a price.' }
    if (exitsOn && stops[0] && s0 == null) return { error: 'Stop 1 needs a price.' }
    return {
      spec: {
        symbol: sym,
        action: buy ? 'BUY' : 'SELL',
        quantity,
        orderType: kind,
        ...(withLimit ? { limitPrice: num(limit) } : {}),
        ...(withStop ? { stopPrice: num(stop) } : {}),
        ...(touch ? { triggerPrice: num(stop) } : {}),
        ...(trailing ? { trail: trailUnit === 'usd' ? { amount: num(trail) } : { percent: num(trail) } } : {}),
        ...(kind === 'TRAIL LIMIT' ? { limitOffset: num(offset) ?? 0.05 } : {}),
        tif,
        ...(tif === 'GTD' ? { goodTill: till.replace('T', ' ').slice(0, 16) } : {}),
        outsideRth: ext,
        ...(t0 != null || s0 != null ? { bracket: { ...(s0 != null ? { stopLoss: s0 } : {}), ...(t0 != null ? { takeProfit: t0 } : {}) } } : {}),
        broker: account!.broker,
      },
    }
  }
  /** The plan as a worker takes it: a ladder of targets and stops, rules, the break-even step. */
  const workerSpec = (): { spec: unknown } | { error: string } => {
    const sizing = sizeMode === 'shares' ? { kind: 'shares', shares: manage && fractions ? Math.round(n * 1e6) / 1e6 : Math.floor(n) } : sizeMode === 'usd' ? { kind: 'notional', usd: n } : sizeMode === 'pct' ? { kind: 'notional', usd: ((account!.buyingPower ?? 0) * n) / 100 } : { kind: 'risk', usd: n }
    const takeProfits: unknown[] = []
    for (let i = 0; i < targets.length; i++) {
      const p = priceOf(targets[i].price, entry)
      if (p == null) return { error: `Target ${i + 1} needs a price.` }
      const pct = num(targets[i].pct)
      const lastRow = i === targets.length - 1
      if (!lastRow && !pct) return { error: `Target ${i + 1} needs its share of the position (%).` }
      takeProfits.push({ price: p, qty: !lastRow && pct! < 100 ? portion(pct!) : { kind: 'rest' } })
    }
    const stopLosses: unknown[] = []
    for (let i = 0; i < stops.length; i++) {
      const s = stops[i]
      const p = priceOf(s.price, entry)
      if (p == null) return { error: `Stop ${i + 1} needs a price.` }
      const extra = num(s.extra)
      if (s.kind !== 'stop' && !extra) return { error: `Stop ${i + 1}: give the ${s.kind === 'limit' ? 'limit price' : 'trailing distance'}.` }
      stopLosses.push({
        price: p,
        // a stop has no share of its own here: the stops part the position evenly, the last one takes the rest
        qty: i === stops.length - 1 ? { kind: 'rest' } : { kind: 'portion', num: 1, den: stops.length },
        ...(s.kind === 'limit' ? { limitPrice: extra } : {}),
        ...(s.kind === 'trail' ? { trail: s.unit === 'usd' ? { amount: extra } : { percent: extra } } : {}),
      })
    }
    const out: unknown[] = []
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i]
      const v = num(r.whenVal)
      let when: unknown
      if (r.when === 'tp') when = { on: 'fill', what: 'tp', tier: Math.max(1, Math.round(v ?? 1)) }
      else if (r.when === 'above' || r.when === 'below') {
        const p = priceOf(r.whenVal, entry)
        if (p == null) return { error: `Rule ${i + 1}: give the price.` }
        when = { on: 'price', op: r.when === 'above' ? '>=' : '<=', value: p }
      } else if (r.when === 'sessions') when = { on: 'sessions', since: 'entryFilled', count: Math.max(1, Math.round(v ?? 1)), at: 'close' }
      else if (r.when === 'minutes') when = { on: 'elapsed', since: 'entryFilled', minutes: Math.max(1, Math.round(v ?? 1)) }
      else if (r.when === 'earnings') when = { on: 'earnings', timing: 'before' }
      else if (r.when === 'time') {
        if (!/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(r.whenVal)) return { error: `Rule ${i + 1}: the time is YYYY-MM-DD HH:MM (New York).` }
        when = { on: 'time', at: r.whenVal.replace('T', ' ').slice(0, 16) }
      } else {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(r.whenVal.trim())) return { error: `Rule ${i + 1}: the time is HH:MM (New York).` }
        when = { on: 'daily', at: r.whenVal.trim() }
      }
      const tv = num(r.thenVal)
      let then: unknown
      if (r.then === 'breakeven') then = { do: 'moveStop', to: 'breakeven' }
      else if (r.then === 'stopTo') {
        const p = priceOf(r.thenVal, entry)
        if (p == null) return { error: `Rule ${i + 1}: give the new stop price.` }
        then = { do: 'moveStop', to: p }
      } else if (r.then === 'trail') {
        const d = distanceOf(r.thenVal)
        if (!d) return { error: `Rule ${i + 1}: give the trailing distance.` }
        then = { do: 'trail', ...d }
      } else if (r.then === 'close') then = { do: 'close' }
      else if (r.then === 'reduce') {
        if (!tv || tv > 100) return { error: `Rule ${i + 1}: how much to sell (1–100%).` }
        then = { do: 'reduce', percent: tv }
      } else if (r.then === 'cancelEntry') then = { do: 'cancelEntry' }
      else then = { do: 'notify', message: r.thenVal.trim() || `${sym}: rule ${i + 1} fired` }
      out.push({ id: `r${i + 1}`, when, then: [then] })
    }
    return {
      spec: {
        name: `${sym} · ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`,
        symbol: sym,
        side: 'long',
        sizing,
        entry: manage
          ? { orderType: 'MKT', tif: 'DAY' }
          : {
              orderType: kind,
              ...(kind === 'LMT' || kind === 'STP LMT' ? { limitPrice: num(limit) } : {}),
              ...(withStop ? { stopPrice: num(stop) } : {}),
              tif,
              ...(tif === 'GTD' ? { goodTill: till.replace('T', ' ').slice(0, 16) } : {}),
              outsideRth: ext,
            },
        takeProfits,
        stopLosses,
        ...(stops.length ? {} : { noStop: true }),
        ...(manage ? { adopt: { avgCost: part?.avgCost ?? last } } : {}),
        exitOutsideRth: exitsExt,
        rules: out,
        // left out, the worker takes the break-even step as Settings has it
        ...(even == null ? {} : { insurance: { on: even } }),
        note: 'Entered on the order ticket.',
        broker: account!.broker,
      },
    }
  }

  const reset = () => (setSize(''), setLimit(''), setStop(''), setTrail(''), setOffset(''), setTargets([]), setStops([]), setRules([]), setFee(''), setNote(''), setOpen({ exits: false, rules: false, notes: false }))
  /** Draft what was filled in and show the system's card for it. Nothing is sent. */
  const review = async () => {
    if (!account) return
    setBusy(true)
    setError(null)
    try {
      const made = asWorker ? workerSpec() : orderSpec()
      if ('error' in made) throw new Error(made.error)
      const d = asWorker ? await workersApi.draft(made.spec) : await ordersApi.draft(made.spec)
      useTicket.setState({ card: { kind: asWorker ? 'worker' : 'order', id: d.id, title: manage ? `Plan for ${name}` : `${buy ? 'Buy' : 'Sell'} ${qtyText(d.plan.quantity)} ${name}`, symbol: code, side: t.side, account: account.id } })
    } catch (e) {
      setError(text(e, 'The order could not be prepared'))
    } finally {
      setBusy(false)
    }
  }
  /** Back to the ticket: a draft that was not approved is dropped (an approved one is untouched: reject refuses it). */
  const back = () => {
    const c = useTicket.getState().card
    if (!c) return
    useTicket.setState({ card: null })
    if (approved) reset()
    else void (c.kind === 'order' ? ordersApi.reject(c.id) : workersApi.reject(c.id)).catch(() => {})
    window.setTimeout(onChanged, 300)
  }
  const record = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.trade({ symbol: code, side: buy ? 'buy' : 'sell', quantity: shares, price: kind === 'LMT' ? (num(limit) ?? last) : last, fee: num(fee) ?? 0, note: note.trim() || undefined, stopLoss: num(sl), takeProfit: num(tp) })
      say(`Recorded: ${buy ? 'bought' : 'sold'} ${qtyText(shares)} ${name}`)
      reset()
      onChanged()
    } catch (e) {
      setError(text(e, 'It could not be recorded'))
    } finally {
      setBusy(false)
    }
  }
  const saveNotes = () => void api.setLevels({ symbol: code, stopLoss: num(sl), takeProfit: num(tp) }).then(() => (say(`Notes saved on ${name}`), onChanged()), (e) => say(text(e, 'The notes could not be saved')))

  // the card is for what the ticket was set to: another stock, side or account drops it
  useEffect(() => {
    if (card && (card.symbol !== code || card.side !== t.side || card.account !== dest)) back()
  }, [card, code, t.side, dest]) // eslint-disable-line react-hooks/exhaustive-deps
  // leaving with a card that was decided: the next visit starts on the ticket again
  const settled = useRef(false)
  settled.current = decided
  useEffect(() => () => void (settled.current && useTicket.setState({ card: null })), [])

  /* the review: the system's own card — the exact orders, the risk, the checks — and Approve on it */
  if (card)
    return (
      <Card className="overflow-hidden">
        <div className="flex h-12 items-center gap-1 border-b border-ws-edge pl-1 pr-3">
          <button type="button" aria-label="Back to the ticket" onClick={back} className="flex h-8 w-8 items-center justify-center rounded-[8px] text-ws-icon transition-colors hover:bg-ws-raise hover:text-ws-fg">
            <ArrowLeft size={15} strokeWidth={2} />
          </button>
          <span className="font-medium text-ws-fg">{card.title}</span>
        </div>
        <div className="px-3 py-3">{card.kind === 'worker' ? <PlanCard id={card.id} /> : <TicketCard id={card.id} />}</div>
      </Card>
    )

  const kinds: readonly (readonly [Kind, string, string])[] = recording ? TYPES.filter(([k]) => k === 'MKT' || k === 'LMT') : TYPES
  const seg = (id: Side, label: string, disabled = false, title?: string) => (
    <button
      key={id}
      type="button"
      aria-pressed={t.side === id}
      disabled={disabled}
      title={title}
      onClick={() => useTicket.setState({ side: id })}
      className={cx('h-full flex-1 rounded-[5px] transition-colors duration-150', t.side === id ? cx('bg-ws-raise font-medium', id === 'buy' ? 'text-ws-up' : id === 'sell' ? 'text-ws-down' : 'text-ws-fg') : disabled ? 'text-ws-fg-5' : 'text-ws-fg-3 hover:text-ws-fg')}
    >
      {label}
    </button>
  )
  const brokers = all.accounts.filter((a) => a.broker !== 'manual')
  const places: (MenuItem | 'rule')[] = [
    ...brokers.map((a): MenuItem => (a.tradable ? { label: a.name, hint: `${a.paper ? 'paper ' : ''}${tail(a)}`, checked: a.id === account?.id, onSelect: () => useTicket.setState({ account: a.id }) } : { label: a.name, hint: 'read only', disabled: true })),
    ...(brokers.length ? (['rule'] as const) : []),
    { label: 'Record only', hint: 'no order is sent', checked: recording, onSelect: () => useTicket.setState({ account: RECORD }) },
  ]
  return (
    <Card>
      {/* which account — or no order at all, only a record of a trade made elsewhere */}
      <div className="flex h-12 items-center border-b border-ws-edge pl-3 pr-1">
        <span className="text-[12px] tracking-normal text-ws-label">Order to</span>
        <Menu
          align="right"
          width={280}
          className="ml-auto min-w-0"
          items={places}
          trigger={() => (
            <button type="button" className="flex h-8 max-w-full items-center gap-2 rounded-[8px] px-2 text-ws-fg transition-colors duration-150 hover:bg-ws-raise">
              {account && <span className={cx('block h-[6px] w-[6px] shrink-0 rounded-full', account.blocked ? 'bg-ws-warn' : 'bg-ws-up')} />}
              <span className="truncate">{account ? account.name : 'Record only'}</span>
              {account?.mask && <span className="shrink-0 text-[12px] tracking-normal text-ws-fg-4">{tail(account)}</span>}
              <ChevronDown size={13} strokeWidth={2} className="shrink-0 text-ws-fg-4" />
            </button>
          )}
        />
      </div>

      <div className="space-y-3 p-3">
        <SymbolSearch value={code} held={all.holdings} onSelect={(s) => useTicket.setState({ symbol: s })} />

        <div className="flex items-center gap-2">
          <div className="flex h-8 min-w-0 flex-1 gap-px rounded-[8px] border border-ws-line bg-ws-field p-[3px]">
            {seg('buy', 'Buy')}
            {seg('sell', 'Sell')}
            {!recording && seg('manage', 'Manage', manageable <= 0, manageable <= 0 ? (minded > 0 ? 'A worker already minds these shares' : 'For shares you hold in this account') : 'Hand shares you hold to a worker: targets, stops and rules')}
          </div>
          {!manage && <Choose value={kind} options={kinds} onChange={(k) => (setKind(k), (k === 'LMT' || k === 'STP LMT' || k === 'LIT') && !limit && last > 0 && setLimit(last.toFixed(2)))} width={320} />}
        </div>

        {/* how much */}
        <div className="rounded-[8px] border border-ws-line bg-ws-field px-3 pb-2 pt-[6px] transition-colors duration-150 focus-within:border-ws-focus">
          <div className="-ml-[6px] flex h-6 items-center gap-px">
            {modes.map(([id, label]) => (
              <button key={id} type="button" aria-pressed={sizeMode === id} onClick={() => setSizeMode(id)} className={cx('h-5 rounded-[4px] px-[6px] text-[11px] leading-none tracking-normal transition-colors duration-150', sizeMode === id ? 'bg-ws-raise text-ws-fg' : 'text-ws-fg-4 hover:text-ws-fg-2')}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex h-10 items-center gap-2">
            <input value={size} onChange={(e) => setSize(e.target.value)} placeholder="0" inputMode="decimal" aria-label="Amount" className="h-full min-w-0 flex-1 text-[24px] font-medium tracking-[-0.02em] tabular-nums placeholder:text-ws-fg-5" />
            <span className="shrink-0 text-[15px] font-medium tracking-[-0.01em] text-ws-fg-3">{sizeMode === 'shares' ? name : sizeMode === 'pct' ? '%' : 'USD'}</span>
          </div>
          <div className="flex h-4 items-center justify-between text-[12px] leading-4 tracking-normal text-ws-fg-3">
            <span>
              In this account <span className="tabular-nums text-ws-fg-2">{qtyText(held)}</span>
            </span>
            {sizeMode !== 'shares' && shares > 0 && <span className="tabular-nums">≈ {qtyText(Number(shares.toFixed(6)))} shares</span>}
          </div>
        </div>

        <div className="divide-y divide-ws-edge border-y border-ws-edge">
          <Line label="Market price">
            {session !== 'regular' && <span className="rounded-[4px] bg-ws-bg px-[5px] py-[2px] text-[10.5px] leading-none text-ws-fg-3">{SESSION[session]}</span>}
            <span className="tabular-nums text-ws-fg">{last ? usd(last) : '—'}</span>
            {code && page !== code && (
              <button type="button" aria-label="Open chart" title="Open chart" onClick={() => useDemo.getState().openStock(code)} className="-mr-1 flex h-6 w-6 items-center justify-center rounded-[6px] text-ws-icon transition-colors hover:bg-ws-raise hover:text-ws-fg">
                <ChartLine size={14} strokeWidth={2} />
              </button>
            )}
          </Line>
          {!manage && (withStop || touch) && (
            <Line label={touch ? 'When the price touches' : 'Stop price'}>
              <input value={stop} onChange={(e) => setStop(e.target.value)} inputMode="decimal" placeholder="0.00" className={cx(field, 'w-24 text-right')} />
            </Line>
          )}
          {!manage && withLimit && (
            <Line label="Limit price">
              <input value={limit} onChange={(e) => setLimit(e.target.value)} inputMode="decimal" placeholder={last ? last.toFixed(2) : '0.00'} className={cx(field, 'w-24 text-right')} />
            </Line>
          )}
          {!manage && trailing && (
            <Line label="Trails the price by">
              <input value={trail} onChange={(e) => setTrail(e.target.value)} inputMode="decimal" placeholder="2" className={cx(field, 'w-16 text-right')} />
              <Choose value={trailUnit} options={UNITS} onChange={setTrailUnit} width={96} />
            </Line>
          )}
          {!manage && kind === 'TRAIL LIMIT' && (
            <Line label="Limit this far past the stop">
              <input value={offset} onChange={(e) => setOffset(e.target.value)} inputMode="decimal" placeholder="0.05" className={cx(field, 'w-24 text-right')} />
            </Line>
          )}
          {!manage && !recording && (
            <Line label="Lasts">
              <Choose value={tif} options={TIFS} onChange={(v) => (setTif(v), v === 'GTD' && !till && setTill(etNow()))} width={180} />
            </Line>
          )}
          {!manage && !recording && tif === 'GTD' && (
            <Line label="Until">
              <input value={till} onChange={(e) => setTill(e.target.value)} placeholder="YYYY-MM-DD HH:MM" className={cx(field, 'w-[150px] text-right')} />
            </Line>
          )}
          {!manage && !recording && (
            <Line label="Extended hours">
              <Tick on={ext} onChange={setExt} title="Pre-market and after-hours. Limit orders only.">
                Pre-market and after hours
              </Tick>
            </Line>
          )}
        </div>
      </div>

      {!recording && buy && (
        <>
          <Fold title="Take profit · stop loss" hint="none" count={targets.length + stops.length} open={open.exits} onToggle={() => setOpen((o) => ({ ...o, exits: !o.exits }))}>
            {targets.map((x, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-12 shrink-0 text-[12px] tracking-normal text-ws-up">Target</span>
                <input value={x.price} onChange={(e) => setTargets((a) => a.map((y, k) => (k === i ? { ...y, price: e.target.value } : y)))} placeholder="price or +%" className={cx(field, 'flex-1')} />
                <input value={i === targets.length - 1 ? '' : x.pct} disabled={i === targets.length - 1} onChange={(e) => setTargets((a) => a.map((y, k) => (k === i ? { ...y, pct: e.target.value } : y)))} placeholder={i === targets.length - 1 ? 'the rest' : '% of shares'} className={cx(field, 'w-[92px] disabled:text-ws-fg-4')} />
                <Drop label="Remove this target" onClick={() => setTargets((a) => a.filter((_, k) => k !== i))} />
              </div>
            ))}
            {stops.map((x, i) => {
              const patch = (p: Partial<Stop>) => setStops((a) => a.map((y, k) => (k === i ? { ...y, ...p } : y)))
              return (
                <Fragment key={i}>
                  <div className="flex items-center gap-2">
                    <span className="w-12 shrink-0 text-[12px] tracking-normal text-ws-down">Stop</span>
                    <input value={x.price} onChange={(e) => patch({ price: e.target.value })} placeholder="price or -%" className={cx(field, 'flex-1')} />
                    <span className="flex w-[92px] shrink-0 justify-end">
                      <Choose value={x.kind} options={STOPS} onChange={(v) => patch({ kind: v, extra: '' })} width={150} />
                    </span>
                    <Drop label="Remove this stop" onClick={() => setStops((a) => a.filter((_, k) => k !== i))} />
                  </div>
                  {/* a stop limit needs the worst price it may sell at, a trailing stop how far it follows */}
                  {x.kind !== 'stop' && (
                    <div className="flex items-center gap-2">
                      <span className="w-12 shrink-0" />
                      <input value={x.extra} onChange={(e) => patch({ extra: e.target.value })} inputMode="decimal" placeholder={x.kind === 'limit' ? 'limit price' : 'trails by'} className={cx(field, 'flex-1')} />
                      <span className="flex w-[92px] shrink-0 justify-end">{x.kind === 'trail' && <Choose value={x.unit} options={UNITS} onChange={(v) => patch({ unit: v })} width={96} />}</span>
                      <span className="w-6 shrink-0" />
                    </div>
                  )}
                </Fragment>
              )
            })}
            <div className="flex items-center gap-4">
              {targets.length < MOST.exits && <Add onClick={() => setTargets((a) => [...a, { price: '', pct: '' }])}>Target</Add>}
              {stops.length < MOST.exits && <Add onClick={() => setStops((a) => [...a, { price: '', kind: 'stop', extra: '', unit: 'pct' }])}>Stop</Add>}
            </div>
            {targets.length + stops.length > 0 && (
              <div className="border-t border-ws-edge pt-2">
                <Tick on={insured} onChange={setEven} title="Once the price has covered most of the way to a target, that lot’s stop moves to break-even (fees included).">
                  Move the stop to break-even near a target
                </Tick>
                <Tick on={exitsExt} onChange={setExitsExt} title="Let the targets and stops work in pre-market and after-hours too.">
                  Work in extended hours too
                </Tick>
              </div>
            )}
          </Fold>

          <Fold title="Automation" hint="no rules" count={rules.length} open={open.rules} onToggle={() => setOpen((o) => ({ ...o, rules: !o.rules }))}>
            {rules.map((r, i) => {
              const w = WHEN.find((x) => x[0] === r.when)!
              const th = THEN.find((x) => x[0] === r.then)!
              const patch = (p: Partial<Rule>) => setRules((a) => a.map((y, k) => (k === i ? { ...y, ...p } : y)))
              return (
                <div key={i} className="rounded-[8px] border border-ws-edge px-2 py-1">
                  <div className="flex min-h-8 items-center gap-1">
                    <span className="w-10 shrink-0 pl-1 text-[12px] tracking-normal text-ws-fg-4">When</span>
                    <span className="min-w-0 flex-1">
                      <Choose value={r.when} options={WHEN} onChange={(v) => patch({ when: v, whenVal: v === 'tp' ? '1' : '' })} align="left" width={240} />
                    </span>
                    {w[2] && <input value={r.whenVal} onChange={(e) => patch({ whenVal: e.target.value })} placeholder={w[2]} className={cx(field, r.when === 'time' ? 'w-[132px]' : 'w-[84px]')} />}
                    <Drop label="Remove this rule" onClick={() => setRules((a) => a.filter((_, k) => k !== i))} />
                  </div>
                  <div className="flex min-h-8 items-center gap-1">
                    <span className="w-10 shrink-0 pl-1 text-[12px] tracking-normal text-ws-fg-4">Then</span>
                    <span className="min-w-0 flex-1">
                      <Choose value={r.then} options={THEN} onChange={(v) => patch({ then: v, thenVal: '' })} align="left" width={240} />
                    </span>
                    {th[2] && <input value={r.thenVal} onChange={(e) => patch({ thenVal: e.target.value })} placeholder={th[2]} title={r.then === 'trail' ? 'In dollars, or as a percent: 2%' : undefined} className={cx(field, r.then === 'notify' ? 'w-[132px]' : 'w-[84px]')} />}
                    <span className="w-6 shrink-0" />
                  </div>
                </div>
              )
            })}
            {rules.length < MOST.rules && <Add onClick={() => setRules((a) => [...a, { when: 'tp', whenVal: '1', then: 'breakeven', thenVal: '' }])}>Rule</Add>}
          </Fold>
        </>
      )}

      <Fold title="Notes" hint={recording ? 'fee, note' : 'levels to watch'} open={open.notes} onToggle={() => setOpen((o) => ({ ...o, notes: !o.notes }))}>
        <div className="flex gap-2">
          <input value={sl} onChange={(e) => setSl(e.target.value)} placeholder="Stop loss" inputMode="decimal" className={cx(field, 'flex-1')} />
          <input value={tp} onChange={(e) => setTp(e.target.value)} placeholder="Take profit" inputMode="decimal" className={cx(field, 'flex-1')} />
        </div>
        {recording && (
          <div className="flex gap-2">
            <input value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Fee" inputMode="decimal" className={cx(field, 'w-[92px]')} />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className={cx(field, 'flex-1')} />
          </div>
        )}
        {/* the notes are kept on a stock that is held; on a trade being recorded they go with the record */}
        <Btn variant="outline" onPanel className="h-7 w-full text-[12px] tracking-normal" disabled={!holding || (num(sl) === holding.stopLoss && num(tp) === holding.takeProfit)} title={holding ? undefined : recording ? 'They are saved with the trade you record' : 'For a stock you hold'} onClick={saveNotes}>
          Save notes on {name}
        </Btn>
      </Fold>

      <div className="space-y-2 border-t border-ws-edge p-3">
        <div className="flex h-5 items-center justify-between text-[13px] tracking-normal">
          <span className="text-ws-fg-3">{manage ? 'Value managed' : 'Estimated total'}</span>
          <span className="font-medium tabular-nums text-ws-fg">{usd(total)}</span>
        </div>
        {manage ? (
          <p className="m-0 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">Nothing is bought: a worker takes over these shares and runs their targets, stops and rules.</p>
        ) : (
          asWorker && <p className="m-0 text-[12px] leading-[17px] tracking-normal text-ws-fg-3">Runs as a worker: it places the orders, then minds the targets, stops and rules by itself.</p>
        )}
        {[...problems, ...(over ? [over] : []), ...(error ? [error] : [])].map((p) => (
          <p key={p} className="m-0 text-[12px] leading-[17px] tracking-normal text-ws-down">
            {p}
          </p>
        ))}
        {recording ? (
          <Btn variant="white" className="h-9 w-full" disabled={busy || !code || !(shares > 0) || !(last > 0 || (kind === 'LMT' && !!num(limit)))} onClick={() => void record()}>
            Record {buy ? 'buy' : 'sale'} {sym}
          </Btn>
        ) : (
          <Btn variant="white" className="h-9 w-full" disabled={busy || !ready} onClick={() => void review()}>
            {busy ? 'Preparing…' : manage ? `Review plan for ${name}` : `Review ${buy ? 'buy' : 'sell'} ${sym}`}
          </Btn>
        )}
        {!recording && <p className="m-0 text-center text-[12px] leading-4 tracking-normal text-ws-fg-4">Nothing is sent until you approve the card</p>}
      </div>
    </Card>
  )
}
